import { useWishlist } from "@/providers/WishlistProvider.tsx";
import {useEffect, useState} from "react";
import {Outlet, useLocation, useParams} from "react-router-dom";
import {AxiosError} from "axios";
import {getWishlist} from "@/api/apiMethods.ts";
import {Avatar, Breadcrumbs} from "@telegram-apps/telegram-ui";
import {Link} from "@/components/Link/Link.tsx";
import {useTranslation} from "react-i18next";
import {useAuthUser} from "@/providers";
import {ErrorState} from "@/components/ErrorState.tsx";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {FullScreenLoader} from "@/components/loader/FullScreenLoader.tsx";
import {getUserAcronym} from "@/helpers/getUserAcronym.ts";

export const WishlistLayout = () => {
  const { authUser } = useAuthUser();
  const { wishlist, setWishlist } = useWishlist();
  const { wishlistId } = useParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const {navigateToRoute} = useNavigation();

  const [ statusCode, setStatusCode ] = useState<number | null>(null);

  useEffect(() => {
    refreshWishlist().then(()=>{});
  }, [setWishlist, wishlistId]);

  const refreshWishlist = async () => {
    if (!wishlistId) {
      setStatusCode(404);
      return;
    }
    const fetchWishlist = async () => {
      return await getWishlist(wishlistId);
    };
    fetchWishlist().then((wishlist)=>{
      if (wishlist.owner.id === authUser?.id && pathname.substring(0, 5) !== "/main") {
        navigateToRoute(`/main${pathname}`, true);
        return;
      }

      setStatusCode(200);
      setWishlist(wishlist);
    }).catch((error) => {
      if (error instanceof AxiosError) {
        setStatusCode(error.response?.status || 500);
      } else {
        setStatusCode(500);
      }
    });
  }

  if (!statusCode) {
    return <FullScreenLoader/>;
  }

  if (statusCode === 403) {
    return <div>Access denied</div>;
  }

  if (statusCode === 404) {
    return <ErrorState title={""} description={t("state.wishlistNotFound")}/>;
  }

  if (!wishlist) {
    throw Error(`Unknown wishlist state (status ${statusCode})`);
  }

  const getTextMaxLen = (text: string, maxLen: number) => {
    if (text.length > maxLen) {
      return text.substring(0, maxLen) + "...";
    }
    return text;
  }

  const isOwner = wishlist.owner.id === authUser?.id;

  if (isOwner) {
    return <div className={"mr-3 ml-3 flex flex-col gap-3"}>
      <Breadcrumbs>
        <Breadcrumbs.Item><Link to={"/main"}>{t('navigation.main')}</Link></Breadcrumbs.Item>
        <Breadcrumbs.Item>
          {getTextMaxLen(wishlist.title, 23)}
        </Breadcrumbs.Item>
      </Breadcrumbs>
      < Outlet context={{wishlist, setWishlist, refreshWishlist}}/>
    </div>
  }

  return (<div className={"mr-3 ml-3 flex flex-col gap-3"}>
    <Breadcrumbs divider={"slash"}>
      <Breadcrumbs.Item><Link to={"/main"}>{t('navigation.main')}</Link></Breadcrumbs.Item>
      <Breadcrumbs.Item>
        <Link className={"flex justify-between gap-2"} to={`/profiles/${wishlist.owner.id}`}>
          <Avatar src={wishlist.owner.photo?.sizes[0]?.link}
                  acronym={getUserAcronym(wishlist.owner.fullname)}
                  size={20}/>
          {getTextMaxLen(wishlist.owner.fullname, 12)}
        </Link>
      </Breadcrumbs.Item>
      <Breadcrumbs.Item>
        {getTextMaxLen(wishlist.title, 8)}
      </Breadcrumbs.Item>
    </Breadcrumbs>
    < Outlet context={{wishlist, setWishlist, refreshWishlist}}/>
  </div>);
}
